import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ILayoutConfigBase } from 'redux-state/layout/types.layout.js';
import {
  ISearchResult,
  ISearchResultsGrouped,
  ISearchState,
  RECENT_SEARCHES_KEY,
  RECENT_SEARCHES_MAX,
  SearchStatus,
} from 'redux-state/search/types.js';
import { getLocalStorageItem, setLocalStorageItem } from 'utilities/helpers/local-storage.js';
import { getDocument } from 'utilities/window.js';

const initialState: ISearchState = {
  isSubLocation: false,
  displaySearchOverlay: false,
  recentSearches: getLocalStorageItem(RECENT_SEARCHES_KEY) || [],
  offsetTop: 0,
  relevantLocations: [],
  searchMCPUrl: undefined,
};

export type SearchAsyncReducerType = PayloadAction<{ query: string }>;

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    REDUX_SEARCH_ASYNC: (state, _: SearchAsyncReducerType) => {
      return { ...state, searchStatus: SearchStatus.SEARCHING };
    },
    REDUX_SEARCH_SAVE_RECENT_SEARCH: (state, action: PayloadAction<{ searchResult: ISearchResult }>) => {
      const searchResult = action.payload.searchResult;
      if (!state.recentSearches.find((recentSearch) => recentSearch.title === searchResult.title)) {
        const newRecentSearches = [searchResult, ...state.recentSearches].slice(0, RECENT_SEARCHES_MAX);
        setLocalStorageItem(RECENT_SEARCHES_KEY, newRecentSearches);
        return { ...state, recentSearches: newRecentSearches };
      }
      // already in recent searches
      return state;
    },
    REDUX_SEARCH_SUCCESS: (state, action: PayloadAction<{ results: ISearchResultsGrouped }>) => {
      return {
        ...state,
        searchStatus: undefined,
        results: action.payload.results,
      };
    },
    REDUX_SEARCH_ERROR: (state) => {
      return state;
    },

    REDUX_SEARCH_SET_RELEVANT_LOCATIONS: (state, action: PayloadAction<{ search: ILayoutConfigBase['search'] }>) => {
      return {
        ...state,
        relevantLocations: action.payload.search?.searchLocations ?? [],
        searchMCPUrl: action.payload.search?.dataUrl,
      };
    },
    REDUX_SEARCH_CLEAR_RESULTS: (state) => {
      return {
        ...state,
        results: undefined,
      };
    },
    REDUX_SEARCH_OPEN_OVERLAY: (state, action: PayloadAction<{ offsetTop?: number }>) => {
      const classList = getDocument()?.body?.classList;
      classList?.add?.('SearchOverlayActive');
      return {
        ...state,
        displaySearchOverlay: true,
        offsetTop: action.payload.offsetTop ?? 0,
      };
    },
    REDUX_SEARCH_CLOSE_OVERLAY: (state) => {
      const classList = getDocument()?.body?.classList;
      classList?.remove?.('SearchOverlayActive');
      return {
        ...state,
        displaySearchOverlay: false,
        offsetTop: 0,
      };
    },
  },
});

export const {
  REDUX_SEARCH_ASYNC,
  REDUX_SEARCH_SAVE_RECENT_SEARCH,
  REDUX_SEARCH_SUCCESS,
  REDUX_SEARCH_ERROR,
  REDUX_SEARCH_SET_RELEVANT_LOCATIONS,
  REDUX_SEARCH_CLEAR_RESULTS,
  REDUX_SEARCH_OPEN_OVERLAY,
  REDUX_SEARCH_CLOSE_OVERLAY,
} = searchSlice.actions;

export const searchReducer = searchSlice.reducer;
